import React, { useEffect, useState } from "react";
import { ThumbtackAPI } from "../api";
import "./ThumbtackLeads.css";

const STATUS_OPTIONS = [
  ["new", "New"],
  ["contacted", "Contacted"],
  ["follow_up", "Follow-up"],
  ["qualified", "Qualified"],
  ["estimate_sent", "Estimate sent"],
  ["won", "Won"],
  ["lost", "Lost"],
  ["spam", "Spam"],
];

const ACTIVITY_OPTIONS = [
  ["note", "Note"],
  ["call", "Called"],
  ["text", "Texted"],
  ["email", "Emailed"],
  ["appointment", "Appointment"],
  ["estimate_sent", "Estimate sent"],
  ["follow_up", "Follow-up"],
];

function displayDate(value) {
  return value ? new Date(value).toLocaleString() : "—";
}

function statusLabel(value) {
  return STATUS_OPTIONS.find(([key]) => key === value)?.[1] || value;
}

function toLocalInput(value) {
  if (!value) return "";
  const date = new Date(value);
  const offset = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
}

export default function ThumbtackLeads() {
  const [leads, setLeads] = useState([]);
  const [selected, setSelected] = useState(null);
  const [statusFilter, setStatusFilter] = useState("");
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [archivedOnly, setArchivedOnly] = useState(false);
  const [leadSearch, setLeadSearch] = useState("");
  const [activityType, setActivityType] = useState("note");
  const [activityText, setActivityText] = useState("");
  const [followUp, setFollowUp] = useState("");
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [message, setMessage] = useState("");

  async function loadDetail(leadId) {
    const detail = await ThumbtackAPI.lead(leadId);
    setSelected(detail);
    setFollowUp(toLocalInput(detail.follow_up_at));
    return detail;
  }

  async function loadInbox(preferredLeadId) {
    const items = await ThumbtackAPI.leads({
      status: statusFilter,
      unread: unreadOnly ? true : "",
      archived: archivedOnly ? true : "",
      q: leadSearch.trim(),
    });
    setLeads(items);

    const nextId = (
      preferredLeadId &&
      items.some((item) => item.id === preferredLeadId)
    )
      ? preferredLeadId
      : items[0]?.id;

    if (nextId) {
      await loadDetail(nextId);
    } else {
      setSelected(null);
    }
  }

  useEffect(() => {
    let active = true;

    ThumbtackAPI.leads()
      .then(async (items) => {
        if (!active) return;
        setLeads(items);
        if (items[0]) {
          const detail = await ThumbtackAPI.lead(items[0].id);
          if (active) {
            setSelected(detail);
            setFollowUp(toLocalInput(detail.follow_up_at));
          }
        }
      })
      .catch(() => {
        if (active) setMessage("Could not load Thumbtack leads.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, []);

  async function applyFilters(event) {
    event.preventDefault();
    setLoading(true);
    setMessage("");
    try {
      await loadInbox();
    } catch {
      setMessage("Could not filter the lead inbox.");
    } finally {
      setLoading(false);
    }
  }

  async function chooseLead(leadId) {
    setWorking(true);
    setMessage("");
    try {
      await loadDetail(leadId);
    } catch {
      setMessage("Could not load this lead.");
    } finally {
      setWorking(false);
    }
  }

  async function updateLead(payload) {
    if (!selected) return;
    setWorking(true);
    setMessage("");
    try {
      await ThumbtackAPI.updateLead(selected.id, payload);
      await loadInbox(selected.id);
    } catch {
      setMessage("Could not update this lead.");
    } finally {
      setWorking(false);
    }
  }

  async function saveFollowUp() {
    await updateLead({
      follow_up_at: followUp
        ? new Date(followUp).toISOString()
        : null,
    });
  }

  async function addActivity(event) {
    event.preventDefault();
    if (!selected) return;

    setWorking(true);
    setMessage("");
    try {
      await ThumbtackAPI.addLeadActivity(selected.id, {
        activity_type: activityType,
        content: activityText.trim() || null,
      });
      setActivityText("");
      await loadInbox(selected.id);
      setMessage("Lead activity saved.");
    } catch {
      setMessage("Could not save the lead activity.");
    } finally {
      setWorking(false);
    }
  }

  async function convertLead() {
    if (!selected) return;
    if (!window.confirm(
      "Convert this Thumbtack lead into a client and prospect job?"
    )) {
      return;
    }

    setWorking(true);
    setMessage("");
    try {
      await ThumbtackAPI.convertLead(selected.id);
      await loadInbox(selected.id);
      setMessage("Lead converted to a client and prospect job.");
    } catch {
      setMessage("Could not convert this lead.");
    } finally {
      setWorking(false);
    }
  }

  if (loading) {
    return <div className="tl-page">Loading Thumbtack leads…</div>;
  }

  return (
    <div className="tl-page">
      <header className="tl-heading">
        <div>
          <div className="tl-eyebrow">Lead desk</div>
          <h1>Thumbtack Leads</h1>
          <p>
            Newest activity appears first. Add notes, record what you did,
            schedule follow-ups, and convert qualified leads deliberately.
          </p>
        </div>
        <a className="tl-setup-link" href="/integrations/thumbtack">
          Connection setup
        </a>
      </header>

      {message && <div className="tl-message">{message}</div>}

      <form className="tl-filters" onSubmit={applyFilters}>
        <label>
          <span>Search</span>
          <input
            aria-label="Search leads"
            value={leadSearch}
            placeholder="Name, phone, city, trade"
            onChange={(event) => setLeadSearch(event.target.value)}
          />
        </label>
        <label>
          <span>Status</span>
          <select
            aria-label="Filter by status"
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value)}
          >
            <option value="">All statuses</option>
            {STATUS_OPTIONS.map(([value, label]) => (
              <option value={value} key={value}>{label}</option>
            ))}
          </select>
        </label>

        <label className="tl-unread-filter">
          <input
            type="checkbox"
            checked={unreadOnly}
            onChange={(event) => setUnreadOnly(event.target.checked)}
          />
          Unread only
        </label>

        <label className="tl-unread-filter">
          <input
            type="checkbox"
            aria-label="Archived"
            checked={archivedOnly}
            onChange={(event) => setArchivedOnly(event.target.checked)}
          />
          Archived
        </label>

        <button type="submit" disabled={working}>Apply filters</button>
      </form>

      {leads.length === 0 ? (
        <section className="tl-empty">
          <h2>{archivedOnly ? "No archived leads" : "No Thumbtack leads yet"}</h2>
          <p>
            {archivedOnly
              ? "Archived leads stay here until you restore one."
              : "New lead deliveries will appear here automatically with the newest activity at the top."}
          </p>
        </section>
      ) : (
        <div className="tl-workspace">
          <section className="tl-list" aria-label="Thumbtack lead inbox">
            {leads.map((lead) => (
              <button
                type="button"
                data-testid="lead-card"
                className={
                  `tl-lead-card ${selected?.id === lead.id ? "is-selected" : ""}`
                }
                key={lead.id}
                onClick={() => chooseLead(lead.id)}
              >
                <div className="tl-lead-topline">
                  <strong>{lead.customer_name || "Unnamed customer"}</strong>
                  {lead.unread && <span className="tl-unread">New</span>}
                </div>
                <span>{lead.service_category || "Thumbtack inquiry"}</span>
                <small>{lead.location_text || "Location not supplied"}</small>
                <div className="tl-lead-meta">
                  <span className={`tl-status status-${lead.status}`}>
                    {statusLabel(lead.status)}
                  </span>
                  {lead.archived_at && <span className="tl-status">Archived</span>}
                  <time>{displayDate(lead.last_activity_at)}</time>
                </div>
                {lead.follow_up_at && (
                  <small className={lead.follow_up_overdue ? "is-overdue" : ""}>
                    Follow-up: {displayDate(lead.follow_up_at)}
                  </small>
                )}
              </button>
            ))}
          </section>

          {selected && (
            <section className="tl-detail">
              <div className="tl-detail-heading">
                <div>
                  <div className="tl-eyebrow">Lead details</div>
                  <h2>{selected.customer_name || "Unnamed customer"}</h2>
                  <p>{selected.service_category || "Thumbtack inquiry"}</p>
                  {selected.archived_at && (
                    <p>Archived {displayDate(selected.archived_at)}</p>
                  )}
                </div>
                <div className="tl-detail-actions">
                  <button
                    type="button"
                    className="tl-secondary"
                    disabled={working}
                    onClick={() => updateLead({ unread: !selected.unread })}
                  >
                    Mark {selected.unread ? "read" : "unread"}
                  </button>
                  <button
                    type="button"
                    className="tl-secondary"
                    disabled={working}
                    onClick={() => updateLead({ archived: !selected.archived_at })}
                  >
                    {selected.archived_at ? "Restore" : "Archive"}
                  </button>
                </div>
              </div>

              <dl className="tl-facts">
                <dt>Phone</dt>
                <dd>{selected.customer_phone || "Not supplied"}</dd>
                <dt>Email</dt>
                <dd>{selected.customer_email || "Not supplied"}</dd>
                <dt>Location</dt>
                <dd>{selected.location_text || "Not supplied"}</dd>
                <dt>Lead price</dt>
                <dd>{selected.lead_price || "Not supplied"}</dd>
                <dt>Charge state</dt>
                <dd>{selected.charge_state || "Not supplied"}</dd>
                <dt>Request</dt>
                <dd>{selected.service_description || "No description supplied"}</dd>
              </dl>

              <div className="tl-controls">
                <label>
                  <span>Lead status</span>
                  <select
                    aria-label="Lead status"
                    value={selected.status}
                    disabled={working}
                    onChange={(event) => updateLead({
                      status: event.target.value,
                    })}
                  >
                    {STATUS_OPTIONS.map(([value, label]) => (
                      <option value={value} key={value}>{label}</option>
                    ))}
                  </select>
                </label>

                <label>
                  <span>Follow-up date and time</span>
                  <input
                    aria-label="Follow-up date and time"
                    type="datetime-local"
                    value={followUp}
                    onChange={(event) => setFollowUp(event.target.value)}
                  />
                </label>

                <button
                  type="button"
                  className="tl-secondary"
                  disabled={working}
                  onClick={saveFollowUp}
                >
                  Save follow-up
                </button>
              </div>

              <form className="tl-activity-form" onSubmit={addActivity}>
                <h3>Record what you did</h3>
                <div>
                  <select
                    aria-label="Activity type"
                    value={activityType}
                    onChange={(event) => setActivityType(event.target.value)}
                  >
                    {ACTIVITY_OPTIONS.map(([value, label]) => (
                      <option value={value} key={value}>{label}</option>
                    ))}
                  </select>
                  <textarea
                    aria-label="Activity note"
                    value={activityText}
                    onChange={(event) => setActivityText(event.target.value)}
                    placeholder="Add the note, result, promise, or next step…"
                    rows={3}
                    maxLength={5000}
                  />
                  <button type="submit" disabled={working}>
                    Save activity
                  </button>
                </div>
              </form>

              <div className="tl-conversion">
                {selected.converted_client_id && selected.converted_job_id ? (
                  <>
                    <strong>Converted</strong>
                    <a href={`/clients/${selected.converted_client_id}`}>
                      Open client
                    </a>
                    <a href={`/jobs/${selected.converted_job_id}`}>
                      Open prospect job
                    </a>
                  </>
                ) : (
                  <button
                    type="button"
                    className="tl-convert"
                    disabled={working}
                    onClick={convertLead}
                  >
                    Convert to client + prospect job
                  </button>
                )}
              </div>

              <section className="tl-timeline">
                <h3>Activity timeline — newest first</h3>
                {selected.activities?.length ? (
                  selected.activities.map((activity) => (
                    <article key={activity.id}>
                      <div>
                        <strong>
                          {activity.activity_type.replaceAll("_", " ")}
                        </strong>
                        <time>{displayDate(activity.created_at)}</time>
                      </div>
                      {activity.content && <p>{activity.content}</p>}
                      {activity.actor_name && (
                        <small>Recorded by {activity.actor_name}</small>
                      )}
                    </article>
                  ))
                ) : (
                  <p>No activity has been recorded.</p>
                )}
              </section>
            </section>
          )}
        </div>
      )}
    </div>
  );
}
